import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@ideoz/ui';

const badgeVariants = cva(
  `inline-flex items-center justify-center font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border h-auto gap-2 rounded-md border-border bg-blue-light-50/50 hover:bg-muted/50 transition-glass font-regular text-muted-foreground hover:text-glass-foreground bg-white dark:bg-gray-800`,
  {
    variants: {
      variant: {
        default: `text-primary-700 bg-white border-gray-200 hover:bg-primary-50 hover:text-primary-800 dark:text-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-primary-900/30 dark:hover:text-primary-200`,
        primary: `text-primary-700 bg-white border-primary-200 hover:bg-primary-50 hover:text-primary-800 dark:text-primary-300 dark:bg-primary-950/60 dark:border-primary-800 dark:hover:bg-primary-900/30 dark:hover:text-primary-200`,
        secondary: `text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700/40 dark:hover:text-gray-200`,
        destructive: `text-error-700 bg-error-50 border-error-200 hover:bg-error-100 hover:text-error-800 dark:text-error-300 dark:bg-error-950/60 dark:border-error-800 dark:hover:bg-error-900/30 dark:hover:text-error-200`,
        outline: `text-gray-600 bg-white border-gray-300 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-600 dark:hover:bg-gray-700/40 dark:hover:text-gray-100`,
        success: `text-success-700 bg-success-50 border-success-200 hover:bg-success-100 hover:text-success-800 dark:text-success-300 dark:bg-success-950 dark:border-success-800 dark:hover:bg-success-800/30 dark:hover:text-success-200`,
        warning: `text-warning-600 bg-warning-25 border-warning-100 hover:bg-warning-50 hover:text-warning-700 dark:text-warning-100 dark:bg-warning-950/50 dark:border-warning-700 dark:hover:bg-warning-900/20 dark:hover:text-warning-50`,
        error: `text-error-700 bg-error-50 border-error-200 hover:bg-error-100 hover:text-error-800 dark:text-error-300 dark:bg-error-950 dark:border-error-800 dark:hover:bg-error-900/40 dark:hover:text-error-200`,
        gray: `text-gray-700 bg-gray-100 border-gray-100 hover:bg-gray-200 hover:text-gray-900 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700/60 dark:hover:text-gray-100`,
        info: `text-blue-gray-700 bg-blue-gray-50 border-blue-gray-200 hover:bg-blue-gray-100 hover:text-blue-gray-900 dark:text-blue-gray-300 dark:bg-blue-gray-950 dark:border-blue-gray-800 dark:hover:bg-blue-gray-900 dark:hover:text-blue-gray-100`,
      },

      // Size variants with 12px base font size consideration
      size: {
        xs: 'px-3 py-1 text-sm',
        sm: 'px-4 py-1.5 text-sm',
        md: 'px-2.5 py-1 text-md',
        lg: 'px-3 py-1.5 text-lg',
      },

      // Icon variants
      icon: {
        false: '',
        dot: 'gap-1.5',
        'x-close': 'gap-1.5',
        'icon-right': 'gap-1.5',
        'icon-left': 'gap-1.5',
        only: 'p-0 aspect-square',
        country: 'gap-1.5',
        avatar: 'gap-1.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
      icon: false,
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /**
   * Show a dot indicator
   */
  showDot?: boolean;
  /**
   * Show close button
   */
  showClose?: boolean;
  /**
   * Close button click handler
   */
  onClose?: () => void;
  /**
   * Icon to display (left side)
   */
  leftIcon?: React.ReactNode;
  /**
   * Icon to display (right side)
   */
  rightIcon?: React.ReactNode;
  /**
   * Country flag or avatar (renamed to avoid conflict with HTML prefix)
   */
  avatarPrefix?: React.ReactNode;
  /**
   * Children to display
   */
  children?: React.ReactNode;
  /**
   * Children classes
   */
  childrenClasses?: string;
}

function Badge({
  className,
  variant,
  size,
  icon,
  showDot,
  showClose,
  onClose,
  leftIcon,
  rightIcon,
  avatarPrefix,
  children,
  childrenClasses,
  ...props
}: BadgeProps) {
  // Determine icon variant based on props
  const iconVariant = React.useMemo(() => {
    if (typeof icon === 'string') return icon;
    if (showDot) return 'dot';
    if (showClose) return 'x-close';
    if (leftIcon) return 'icon-left';
    if (rightIcon) return 'icon-right';
    if (avatarPrefix) return children ? 'avatar' : 'only';
    if (!children) return 'only';
    return false;
  }, [icon, showDot, showClose, leftIcon, rightIcon, avatarPrefix, children]);

  return (
    <div className={cn(badgeVariants({ variant, size, icon: iconVariant }), className)} {...props}>
      {/* Dot indicator */}
      {(showDot || iconVariant === 'dot') && (
        <div className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" aria-hidden="true" />
      )}

      {/* Left icon or prefix */}
      {leftIcon && (
        <span className="flex-shrink-0" aria-hidden="true">
          {leftIcon}
        </span>
      )}

      {/* Country flag or avatar prefix */}
      {avatarPrefix && (
        <span className="flex-shrink-0" aria-hidden="true">
          {avatarPrefix}
        </span>
      )}

      {/* Text content */}
      {children && (
        <span className={`${childrenClasses ? childrenClasses : 'truncate flex items-center'}`}>
          {children}
        </span>
      )}

      {/* Right icon */}
      {rightIcon && (
        <span className="flex-shrink-0" aria-hidden="true">
          {rightIcon}
        </span>
      )}

      {/* Close button */}
      {(showClose || iconVariant === 'x-close') && (
        <button
          type="button"
          className="flex-shrink-0 ml-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm p-0.5 transition-colors"
          onClick={onClose}
          aria-label="Remove"
        >
          <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
            <path
              d="M9 3L3 9M3 3l6 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

export { Badge, badgeVariants };
